/** QuickTicketing — Préparation des captures jointes à un ticket (dans le navigateur).
 *
 * Une capture d'écran collée pèse vite plusieurs Mo pour un gain nul : le modèle
 * travaille au mieux autour de 1 500 px de grand côté. On réduit donc avant l'envoi
 * (grand côté ≤ 1568 px), en PNG pour une capture (texte net), en JPEG pour une photo.
 */

const MAX_SIDE = 1568;
const MAX_BYTES = 4.5 * 1024 * 1024;
export const MAX_IMAGES = 6;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`« ${file.name || 'image'} » n'est pas une image lisible`)); };
    img.src = url;
  });
}

const toBlob = (canvas, type, q) => new Promise((r) => canvas.toBlob(r, type, q));

function blobToBase64(blob) {
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result).split(',')[1]);
    fr.readAsDataURL(blob);
  });
}

/** Fichier image → { id, name, mediaType, data (base64), preview (URL objet) }. */
export async function prepareImage(file) {
  if (!file.type.startsWith('image/')) throw new Error(`« ${file.name} » n'est pas une image`);
  const img = await loadImage(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);

  // Capture (PNG d'origine) : on garde le PNG si le poids reste raisonnable.
  let blob = file.type === 'image/png' ? await toBlob(canvas, 'image/png') : null;
  if (!blob || blob.size > MAX_BYTES) {
    for (const q of [0.9, 0.8, 0.65, 0.5]) {
      blob = await toBlob(canvas, 'image/jpeg', q);
      if (blob.size <= MAX_BYTES) break;
    }
  }
  if (blob.size > MAX_BYTES) throw new Error(`« ${file.name} » reste trop lourde après réduction`);
  return {
    id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name: file.name || (file.type === 'image/png' ? 'capture.png' : 'image.jpg'),
    mediaType: blob.type,
    data: await blobToBase64(blob),
    preview: URL.createObjectURL(blob),
    size: [w, h],
  };
}

/** Images d'un presse-papiers ou d'un glisser-déposer. */
export function imageFiles(list) {
  const out = [];
  for (const item of list ?? []) {
    const f = item instanceof File ? item : item.kind === 'file' ? item.getAsFile() : null;
    if (f && f.type.startsWith('image/')) out.push(f);
  }
  return out;
}
